import React, { FormEvent, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'

import { Box, FormHelperText, FormLabel, Grid, Link, Typography } from '@material-ui/core'
import { OpenInNewOutlined } from '@material-ui/icons'

import { LabeledInput } from '../../LabeledInput'
import { DefaultRadioGroup } from '../../DefaultRadioGroup'
import { SyncStartTableRow } from '../../SyncStartTableRow'

import { useMessages } from '../../../hooks/snackbarHooks'
import { useWildberriesForm } from './hooks/useWildberriesForm'
import { useApi, useLogic } from '../../../hooks/storeHook'
import { useRadioGroupStyles } from '../../../hooks/radioGroupStylesHook'
import { useSettingsPageStyles } from '../../../hooks/settingsPageStylesHook'

import { appRoutes } from '../../../common/appRoutes'

import { EditWbAccountModel } from '../../../server/mpsklad_core/Models/EditWbAccountModel'
import { WbDeliveryType } from '../../../server/mpsklad_core/Entity/Wildberries/WbDeliveryType'
import { CommissionsRecordingType } from '../../../server/mpsklad_core/Entity/Base/CommissionsRecordingType'
import { useWbAccountOptional } from '../../../hooks/routeHooks'

export const WildberriesApiSettings =
  observer(() => {
    const {showSuccess, showError} = useMessages()
    const logic = useLogic()
    const {
      userSync: {
        setWbSyncStartDate
      }
    } = useApi()

    const account = useWbAccountOptional()
    const history = useHistory()

    const {
      isLoading,
      setIsLoading,
      deliveryType,
      setDeliveryType,
      newToken,
      setNewToken,
      name,
      setName
    } = useWildberriesForm(account)

    const classes = useSettingsPageStyles()
    const radioGroupClasses = useRadioGroupStyles()

    const deliveryTypeOptions =
      useMemo(() => [{
        value: WbDeliveryType.FBS,
        label: 'Продажа со своего склада (FBS)'
      }, {
        value: WbDeliveryType.FBW,
        label: 'Продажа со склада Wildberries (FBW)'
      }, {
        value: WbDeliveryType.DBS,
        label: 'Продажа со своего склада, доставка силами продавца (DBS)'
      }, {
        value: WbDeliveryType.WbGo,
        label: 'Продажа со своего склада, доставка курьером Wildberries (WbGo)'
      }, {
        value: WbDeliveryType.eDBS,
        label: 'Продажа со своего склада, экспресс доставка силами продавца (Express DBS)'
      }, {
        value: WbDeliveryType.ClickCollect,
        label: 'Самовывоз из магазина продавца (Click&Collect)'
      }], [])

    const getDeliveryTypeClasses =
      (checked: boolean) => clsx(radioGroupClasses.radioButton, checked && radioGroupClasses.selectedRadioButton)

    const onSubmit = async (e: FormEvent) => {
      e.preventDefault()

      if (!newToken) {
        showError('Необходимо ввести API токен.')
        return
      }

      setIsLoading(true)

      try {
        const formData = {
          deliveryType,
          tokenCombined: newToken,
          name: name || undefined,
        // TODO: Split models according to UI and actual data being used, don't just re-send the same stuff
          isUpdatePrices: account?.isUpdatePrices ?? false,
          isGeneratingPayments: account?.isGeneratingPayments ?? false,
          isUsingCommissions: account?.isUsingCommissions ?? false,
          ordersCommissionsRecording:
            account?.ordersCommissionsRecording ?? CommissionsRecordingType.Nothing,
          documentsCommissionsRecording:
            account?.documentsCommissionsRecording ?? CommissionsRecordingType.Nothing,
          msSalesReturnStoreId: account?.msSalesReturnStoreId
        } satisfies Omit<EditWbAccountModel, 'id'>

        if (account) {
          await logic.editWbAccount({id: account.id, ...formData})
        } else {
          const newAccount = await logic.createWbAccount({id: 0, ...formData})

          history.push(appRoutes.Store.settings.api.makeUrl({accountId: newAccount.id, integration: 'Wildberries'}))
        }

        showSuccess('Сохранено!')
      } catch (ex) {
        console.error('Failed to save Wildberries account', ex)
        showError('Ошибка при сохранении аккаунта')
      } finally {
        setIsLoading(false)
      }
    }

    return (
      <Box className={classes.pageContainer}>
        <Typography className={classes.header}>Настройки API</Typography>

        <form onSubmit={onSubmit}>
          <Grid container spacing={3}>
            {/* Left column */}
            <Grid item className={classes.wrapper} style={account ? {minWidth: '50%'} : {}}>
              <Grid container spacing={3}>
                {/* API Token */}
                <Grid item xs={12}>
                  <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                    <Grid item className={classes.root} xs={12}>
                      <LabeledInput
                        required
                        id="newToken"
                        label="API токен"
                        placeholder="eyJhbGcc..."
                        value={newToken}
                        onChange={setNewToken}
                        helperText={<>
                          {/* @formatter:off */}
                          Создать токен можно в личном кабинете Wildberries:&nbsp;
                          <Link target="_blank" href="https://seller.wildberries.ru/supplier-settings/access-to-api" className="nowrap">
                            Профиль - Настройки - Доступ к API <OpenInNewOutlined fontSize="inherit"/>
                          </Link>.
                          <br/>
                          Необходимо выбрать категории: Контент, Маркетплейс, Статистика, Цены и скидки.
                          {/* @formatter:on */}
                        </>}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Account Name */}
                <Grid item xs={12}>
                  <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                    <Grid item className={classes.root} xs={12}>
                      <LabeledInput
                        required
                        id="accountName"
                        label="Название аккаунта"
                        placeholder="Wildberries 8922"
                        value={name}
                        onChange={setName}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Schema of Work */}
                <Grid item xs={12}>
                  <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                    <Grid item className={classes.root} xs={12}>
                      <DefaultRadioGroup
                        name="ModelType"
                        label="Схема работы"
                        options={deliveryTypeOptions}
                        value={deliveryType}
                        onChange={setDeliveryType}
                        getOptionLabelClassName={() => radioGroupClasses.radioLabel}
                        getOptionClassName={getDeliveryTypeClasses}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Right column */}
            {
              account &&
              <Grid item xs={12} md={6}>
                <Grid container spacing={3}>
                  {account.ordersTrackStartUnix &&
                   <Grid item xs={12}>
                     <Grid container className={clsx(classes.settingsTopic, 'default-border')}>
                       <Grid item className={classes.root} xs={12}>
                         <Box>
                           <FormLabel component="legend" className={classes.boldLabel}>
                             Дата начала синхронизации заказов
                           </FormLabel>
                         </Box>

                         <SyncStartTableRow account={account} onSubmit={setWbSyncStartDate}/>

                         <FormHelperText className={classes.helperText}>
                           Будут синхронизированы заказы, созданные позже этой даты.
                         </FormHelperText>
                       </Grid>
                     </Grid>
                   </Grid>
                  }
                </Grid>
              </Grid>
            }
          </Grid>

          <Box display="flex">
            <button
              type="submit"
              className={clsx(classes.saveButton, 'default-button')}
              disabled={isLoading}
            >
              <p>{isLoading ? 'Сохраняем...' : 'Сохранить'}</p>
            </button>
          </Box>
        </form>
      </Box>
    )
  })